import React, { useState } from 'react';
import LoginSVG from '@birdi/icons/log-in.svg';
import MenuAlt from '@birdi/icons/hero-menu-alt.svg';
import SettingsSVG from '@birdi/icons/settings.svg';
import LayoutSVG from '@birdi/icons/sidebar.svg';
import SunSVG from '@birdi/icons/sun.svg';
import MaskSVG from '@birdi/icons/mask-happy.svg';
import CheckSVG from '@birdi/icons/check.svg';
import PlusSVG from '@birdi/icons/plus.svg';
import { LoadingSpinner } from '@birdi/loading';
import { ContextMenu2 } from '@birdi/context-menu/context-menu-2';
import { useTheme, BirdiTheme } from '@birdi/theme';
import { stroot, auth } from '@birdi/js-sdk';
import { user, assessmentType } from '@birdi/js-sdk/src/dev';
import { css } from '@emotion/react';
import { MaskModal } from './MaskModal';

//TODO: update plan ids to not be hardcoded
function createOpsUser(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('ez7hah'), {
    skipOnboarding: true,
    ops: true,
    planId: '8434a2c6-bb3f-48bf-9ee1-a70967cbad88',
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createPlusUser(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('k22hhi'), {
    skipOnboarding: true,
    ops: false,
    planId: '00d2dfed-da89-4121-9984-2592d09c34fc',
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createNewAccount(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('k22tti'), {
    skipOnboarding: false,
    ops: false,
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createGrowthUser(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('p22hhp'), {
    skipOnboarding: true,
    ops: false,
    planId: '1428631d-b6ed-4ea7-92a6-2ed9cc3cadc4',
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createUltimateUser(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('l22hhl'), {
    skipOnboarding: true,
    ops: false,
    planId: '8434a2c6-bb3f-48bf-9ee1-a70967cbad88',
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createFreeUser(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('eb7hah'), {
    skipOnboarding: true,
    planId: 'b1e75661-9130-4ead-8ec9-b1c7c46f2878',
    featureFlagOrganisations: ['pro-processing', 'maps-v2'],
  }).finally(() => setLoading(false));
}

function createPilot(setLoading: (loading: boolean) => void) {
  setLoading(true);
  return user.login(stroot('mckplt'), {
    pilot: true,
    skipOnboarding: true,
  }).finally(() => setLoading(false));
}

const buttonCSS = (theme: BirdiTheme) => css`
  display: block;
  width: 100%;
  color: inherit;
  padding: 0.75em 0.5em;
  border-radius: 3px;
  text-align: left;
  white-space: nowrap;
  box-sizing: border-box;
  line-height: inherit;
  &:hover {
    background: ${theme.nightSky};
  }
`;

interface MetaBar {
  id: string;
  masked: boolean;
  scopeRole: string;
  organisation: {
    id?: string;
    name?: string;
  };
  devTools: boolean;
  isOpsUser: boolean;
  missionRoleOverride: string;
  toggleOpsUI: (visibility: boolean) => void;
  opsUIVisible: boolean;
  opsActions: string[];
}

const unmask = () =>
  auth.unmask(stroot('unmask')).then(() => window.location.reload());

export function MetaBar({
  id = '',
  masked = false,
  scopeRole = '',
  organisation = {},
  devTools = process.env.DEVTOOLS as unknown as boolean,
  missionRoleOverride,
  opsActions = [],
}: MetaBar) {
  const [maskModalOpen, setMaskModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toggle: toggleTheme, darkMode } = useTheme();
  if (!devTools && !opsActions.includes('access_ops')) return false;
  return (
    <>
      {isLoading && (
        <div css={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          flex-direction: column;
          gap: 15px;
        `}>
          <LoadingSpinner color="black" size="50px" />
          <div css={css`
            color: white;
            font-size: 18px;
            max-width: 400px;
            text-align: center;
            background-color: rgba(0, 0, 0, 0.7);
            padding: 15px;
            border-radius: 8px;
          `}>
            <span>Setting up integrated account...</span>
          </div>
        </div>
      )}
      <div
        css={(theme: BirdiTheme) => css`
          position: fixed;
          bottom: 2px;
          left: 2px;
          padding: 0.25em 0.75em;
          border-radius: 6px;
          background: rgba(25, 25, 26, 0.9);
          font-size: 0.75em;
          color: ${theme.white};
          z-index: 100;
          span:not(:last-child) {
            margin-right: 0.25em;
          }
        `}
      >
        <div
          css={css`
            width: 10px;
            height: 10px;
            background: ${id ? '#5c9cff' : '#8c4444'};
            display: inline-block;
            border-radius: 8px;
            margin-right: 0.5em;
          `}
        />
        <span
          css={css`
            font-family: monospace;
          `}
        >
          {devTools ? 'Dev' : 'Prod'}
        </span>
        <MaskModal open={maskModalOpen} setOpen={setMaskModalOpen} />
        <ContextMenu2
          Menu={({ hide, wrapperRef }) => (
            <div
              ref={wrapperRef}
              css={(theme: BirdiTheme) => css`
                position: absolute;
                bottom: calc(100% + 0.25em);
                width: 20em;
                max-height: calc(100vh - 5em);
                overflow-y: scroll;
                padding: 0.25em;
                border-radius: 3px;
                background: ${theme.blackbird};
                &:hover svg {
                  color: ${theme.white};
                }
              `}
            >
              {opsActions.includes('access_ops') && (
                <a
                  href={`${process.env.MAP_APP_URL}/missions/plan-managed`}
                  css={buttonCSS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlusSVG
                    css={css`
                      margin-right: 1em;
                    `}
                  />
                  <span>New capture request</span>
                </a>
              )}
              {devTools && (
                <>
                  <button
                    onClick={() => {
                      createNewAccount(setIsLoading);
                      hide();
                    }}
                    css={buttonCSS}
                    disabled={isLoading}
                  >
                    <LoginSVG
                      css={css`
                        margin-right: 1em;
                      `}
                    />
                      <span>Login with new account</span>
                    </button>
                  <button
                    onClick={() => {
                      createFreeUser(setIsLoading);
                      hide();
                    }}
                    css={buttonCSS}
                    disabled={isLoading}
                  >
                    <LoginSVG
                      css={css`
                        margin-right: 1em;
                      `}
                    />
                    <span>Login as free user</span>
                  </button>
                  <button
                  onClick={() => {
                    createPlusUser(setIsLoading);
                    hide();
                  }}
                  css={buttonCSS}
                  disabled={isLoading}
                >
                  <LoginSVG
                    css={css`
                      margin-right: 1em;
                    `}
                  />
                    <span>Login as plus user</span>
                  </button>
                  <button
                    onClick={() => {
                      createGrowthUser(setIsLoading);
                      hide();
                    }}
                    css={buttonCSS}
                    disabled={isLoading}
                  >
                    <LoginSVG
                      css={css`
                        margin-right: 1em;
                      `}
                    />
                      <span>Login as growth user</span>
                    </button>
                    <button
                      onClick={() => {
                        createUltimateUser(setIsLoading);
                        hide();
                      }}
                      css={buttonCSS}
                      disabled={isLoading}
                    >
                      <LoginSVG
                        css={css`
                          margin-right: 1em;
                        `}
                      />
                        <span>Login as ultimate user</span>
                      </button>
                    <button
                      onClick={() => {
                        createOpsUser(setIsLoading);
                        hide();
                      }}
                      css={buttonCSS}
                      disabled={isLoading}
                    >
                      <LoginSVG
                        css={css`
                          margin-right: 1em;
                        `}
                      />
                        <span>Login as ops user</span>
                    </button>
                    <button
                      onClick={() => {
                        createPilot(setIsLoading);
                        hide();
                      }}
                      css={buttonCSS}
                      disabled={isLoading}
                    >
                      <LoginSVG
                        css={css`
                          margin-right: 1em;
                        `}
                      />
                      <span>Login as pilot</span>
                    </button>
                </>
              )}
              {id && (
                <button
                  onClick={() => {
                    setMaskModalOpen(true);
                    hide();
                  }}
                  css={buttonCSS}
                >
                  <MaskSVG
                    css={(theme: BirdiTheme) => css`
                      color: ${theme.white};
                      margin-right: 1em;
                    `}
                  />
                  <span>Mask account</span>
                </button>
              )}
              {devTools && (
                <button css={buttonCSS} onClick={toggleTheme}>
                  <SunSVG
                    css={(theme: BirdiTheme) => css`
                      color: ${theme.white};
                      margin-right: 1em;
                    `}
                  />
                  <span>Experimental Dark Mode</span>
                  {darkMode && <CheckSVG />}
                </button>
              )}
              {devTools && (
                <a
                  href={`${process.env.APP_URL}/dev/mission`}
                  css={buttonCSS}
                  onClick={hide}
                >
                  <SettingsSVG
                    css={css`
                      margin-right: 1em;
                    `}
                  />
                  Test tools
                </a>
              )}
              {devTools && (
                <button
                  type="button"
                  onClick={() => {
                    assessmentType
                      .mockAssessmentTypes(stroot('woce6v'))
                      .then(() => {
                        window.location.href = '/assessment-types';
                      })
                      .catch((err) => console.error(err));
                    hide();
                  }}
                  css={buttonCSS}
                >
                  <span>Add assessment types</span>
                </button>
              )}
              <h4
                css={(theme: BirdiTheme) => css`
                  margin: 0.5em 1em;
                  color: ${theme.white};
                  font-weight: 600;
                `}
              >
                External links
              </h4>
              <a
                href={`${process.env.OPS_APP_URL}`}
                target="_blank"
                rel="noopener noreferrer"
                css={buttonCSS}
                onClick={hide}
              >
                <LayoutSVG
                  css={css`
                    margin-right: 1em;
                  `}
                />
                ops.birdi.io
              </a>
              <hr
                css={(theme) => css`
                  margin: 0.5em 1em 1em 1em;
                  border-top: 1px solid ${theme.nightSky};
                `}
              />
              <div
                css={(theme: BirdiTheme) => css`
                  padding: 0 1em 1em 1em;
                  color: ${theme.white};
                `}
              >
                <div
                  css={css`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    width: 100%;
                  `}
                />
                {scopeRole && <div>{`${scopeRole} @ ${organisation.name}`}</div>}
                {missionRoleOverride && (
                  <div>{`Mission override: ${missionRoleOverride}`}</div>
                )}
                {id ? (
                  <>
                    <span
                      css={css`
                        user-select: none;
                      `}
                    >
                      ID:{' '}
                    </span>
                    <span>{id}</span>
                  </>
                ) : (
                  'Anonymous'
                )}
              </div>
            </div>
          )}
          Button={(props) => (
            <button
              onClick={props.toggle}
              ref={props.toggleRef}
              css={(theme: BirdiTheme) => css`
                color: ${theme.darkGrey};
                ${props.open && 'svg { color: white; }'}
                &:hover svg {
                  color: white;
                }
              `}
              id="dev-tools-menu-button"
            >
              <MenuAlt
                css={css`
                  position: relative;
                  top: 1.5px;
                `}
              />
            </button>
          )}
        />
        {masked && (
          <button
            type="button"
            onClick={unmask}
            css={(theme) => css`
              margin-right: 0.5em;
              color: ${theme.mono70};
              &:hover svg {
                color: white;
              }
            `}
          >
            <MaskSVG />
          </button>
        )}
      </div>
    </>
  );
}
